'use client';

import { useBlogSource, type Article } from '@akademi/site-builder-runtime';
import { resolveBlogHero, type BlogHeroVariant } from './BlogHeroVariants';
import { sectionPad, sectionInner } from '../_shared/styles';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Featured-article hero. By default fetches the newest article and
 * renders the active hero variant. Operators can pin a specific
 * article by slug.
 *
 * <p>Self-padding (sectionPad + sectionInner) so the block looks
 * correct at any body-slot root position.
 */
export interface BlogFeaturedHeroProps {
  variant?: BlogHeroVariant;
  /** Pin a specific article to feature. When empty, fetches the most
   *  recent article. */
  pinnedSlug?: string;
  showAuthor?: boolean;
  showReadTime?: boolean;
  /** href template per article. Default {@code /blog/{slug}}. */
  articleHrefTemplate?: string;
}

function fillTemplate(tpl: string, slug: string): string {
  return tpl.replace('{slug}', encodeURIComponent(slug));
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function prettify(s: string): string {
  return s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase() + p.slice(1))
    .join(' ');
}

export function BlogFeaturedHero({
  variant = 'gradient',
  pinnedSlug,
  showAuthor = true,
  showReadTime = true,
  articleHrefTemplate = '/blog/{slug}',
}: BlogFeaturedHeroProps) {
  const blog = useBlogSource();

  const state = useAsyncResource<Article>(async () => {
    if (!blog) return null;
    if (pinnedSlug) {
      return await blog.getArticleBySlug(pinnedSlug);
    }
    const { items } = await blog.listArticles({
      sort: ['publishedAt:desc'],
      pagination: { page: 1, pageSize: 1 },
    });
    return items[0] ?? null;
  }, [blog, pinnedSlug]);

  if (!blog) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="BlogFeaturedHero" adapterName="BlogSource" />
        </div>
      </BlockRoot>
    );
  }

  if (state.kind !== 'loaded') {
    return null;
  }

  const article = state.data;
  const Hero = resolveBlogHero(variant);
  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        <Hero
          href={fillTemplate(articleHrefTemplate, article.slug)}
          title={article.title}
          excerpt={article.excerpt}
          date={formatDate(article.publishedAt)}
          author={showAuthor ? article.author?.name : undefined}
          authorRole={showAuthor ? article.author?.role : undefined}
          category={article.category ? prettify(article.category) : undefined}
          readTime={showReadTime ? article.readTime : undefined}
          cover={article.cover}
        />
      </div>
    </BlockRoot>
  );
}
